// Utility layer per gestire le timbrature in IndexedDB.
// Tutte le funzioni restituiscono Promise e operano esclusivamente sullo store "timbrate".

import { getDB } from "../utils";

const TIMBRATE_STORE = "timbrate";
const ACTIVE_WINDOW_HOURS = 22; // soglia per individuare la giornata in corso

function ensureIsoString(value) {
	if (!value) return null;
	if (typeof value === "string") return value;
	if (value instanceof Date) return value.toISOString();
	return null;
}

function normalizeTimbrata(record) {
	if (!record) throw new Error("Record timbratura non valido");
	const userId = record.user_id || record.userId || null;
	if (!userId) throw new Error("Timbratura senza utente: impossibile salvarla");
	const orario = ensureIsoString(record.orario) || new Date().toISOString();
	const dataRiferimento = record.data_riferimento || orario.slice(0, 10);
	return {
		id: record.id || `timbrata_${Date.now()}_${Math.random().toString(16).slice(2)}`,
		user_id: userId,
		assunzione_id: record.assunzione_id || null,
		tipo: record.tipo,
		orario,
		data_riferimento: dataRiferimento,
		timeframe_id: record.timeframe_id || null,
		device_id: record.device_id || null,
		qr: record.qr || null,
		qr_decoded: record.qr_decoded || null,
		data_sync: record.data_sync || null,
		fineGiornata: Boolean(record.fineGiornata),
	};
}

export async function addTimbrata(record) {
	const sanitized = normalizeTimbrata(record);
	const db = await getDB();
	await db.put(TIMBRATE_STORE, sanitized);
	return sanitized;
}

export async function addManyTimbrature(records = []) {
	const db = await getDB();
	const tx = db.transaction(TIMBRATE_STORE, "readwrite");
	for (const rec of records) {
		const normalized = normalizeTimbrata(rec);
		tx.store.put(normalized);
	}
	await tx.done;
}

export async function getTimbratureByUser(userId, { includeSynced = true } = {}) {
	const db = await getDB();
	let results = await db.getAll(TIMBRATE_STORE);
	if (userId) {
		results = results.filter(r => r.user_id === userId);
	}
	if (!includeSynced) {
		results = results.filter(r => !r.data_sync);
	}
	return results.sort((a, b) => new Date(a.orario).getTime() - new Date(b.orario).getTime());
}

export async function getTimbratureByDate(userId, date) {
	const db = await getDB();
	let results = [];
	if (date) {
		results = await db.getAllFromIndex(TIMBRATE_STORE, "by_data_riferimento", date);
	} else {
		results = await db.getAll(TIMBRATE_STORE);
	}
	if (userId) {
		results = results.filter(r => r.user_id === userId);
	}
	return results.sort((a, b) => new Date(a.orario).getTime() - new Date(b.orario).getTime());
}

export async function getPendingTimbrature(userId) {
	const results = await getTimbratureByUser(userId, { includeSynced: false });
	console.log("Timbrate non sincronizzate recuperate:", results);
	return results;
}

export async function markTimbrataSynced(id, dateSync = new Date().toISOString()) {
	const db = await getDB();
	const current = await db.get(TIMBRATE_STORE, id);
	if (!current) return null;
	const updated = { ...current, data_sync: dateSync };
	await db.put(TIMBRATE_STORE, updated);
	return updated;
}

export async function deleteTimbratureByIds(ids = []) {
	if (!ids.length) return 0;
	const db = await getDB();
	const tx = db.transaction(TIMBRATE_STORE, "readwrite");
	for (const id of ids) {
		tx.store.delete(id);
	}
	await tx.done;
	return ids.length;
}

export async function clearGiornataIfSynced(userId, date) {
	const records = await getTimbratureByDate(userId, date);
	if (!records.length) {
		return { removed: false, reason: "not-found" };
	}
	const allSynced = records.every(r => r.data_sync);
	if (!allSynced) {
		return { removed: false, reason: "pending" };
	}
	await deleteTimbratureByIds(records.map(r => r.id));
	return { removed: true };
}

export async function getActiveGiornataDate(userId, { referenceDate = new Date(), windowHours = ACTIVE_WINDOW_HOURS } = {}) {
	const all = await getTimbratureByUser(userId);
	if (!all.length) return null;
	const threshold = referenceDate.getTime() - windowHours * 60 * 60 * 1000;
	const grouped = all.reduce((acc, rec) => {
		const bucket = acc.get(rec.data_riferimento) || [];
		bucket.push(rec);
		acc.set(rec.data_riferimento, bucket);
		return acc;
	}, new Map());
	let candidate = null;
	for (const [date, records] of grouped.entries()) {
		const start = records.find(r => r.tipo === "INIZIO_GIORNATA");
		if (!start) continue;
		const end = records.find(r => r.tipo === "FINE_GIORNATA");
		if (end) continue; // giornata chiusa (anche se non ancora sincronizzata)
		const startTime = new Date(start.orario).getTime();
		if (startTime < threshold) continue;
		if (!candidate || new Date(date) > new Date(candidate)) {
			candidate = date;
		}
	}
	return candidate;
}

export async function importLegacyLocalDay(userId, date, timbrature = []) {
	if (!userId || !date || !Array.isArray(timbrature) || !timbrature.length) {
		return { imported: 0 };
	}
	const payloads = timbrature.map(t => ({
		...t,
		user_id: userId,
		data_riferimento: t.data_riferimento || date,
	}));
	await addManyTimbrature(payloads);
	return { imported: payloads.length };
}

export async function upsertTimbrata(record) {
	const sanitized = normalizeTimbrata(record);
	const db = await getDB();
	const existing = await db.get(TIMBRATE_STORE, sanitized.id);
	if (existing) {
		const merged = { ...existing, ...sanitized };
		await db.put(TIMBRATE_STORE, merged);
		return merged;
	}
	await db.put(TIMBRATE_STORE, sanitized);
	return sanitized;
}

export async function purgeAllTimbrature() {
	const db = await getDB();
	await db.clear(TIMBRATE_STORE);
}

// Funzione per inviare timbrate non sincronizzate
export async function sendPendingTimbrature(userId, sendToServer) {
    const pendingTimbrature = await getPendingTimbrature(userId);

    for (const timbrata of pendingTimbrature) {
        try {
            const response = await sendToServer(timbrata);
            if (response.success) {
                // Imposta la data_sync per evitare ulteriori invii
                await markTimbrataSynced(timbrata.id);
            } else {
                console.error(`Errore durante l'invio della timbrata ${timbrata.id}:`, response.error);
            }
        } catch (error) {
            console.error(`Errore di rete durante l'invio della timbrata ${timbrata.id}:`, error);
        }
    }
}
